import type { ChangeEvent } from 'react';

interface FormFieldProps {
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  type?: 'text' | 'email' | 'date' | 'number' | 'select';
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  options?: Array<{ value: string; label: string }>;
  min?: string | number;
  max?: string | number;
  description?: string;
}

/**
 * FormField Component
 *
 * Reusable form field with label, input, and error display.
 * Supports text, email, date, number, and select inputs.
 */
export function FormField({
  label,
  value,
  onChange,
  type = 'text',
  error,
  placeholder,
  disabled = false,
  required = false,
  id,
  options,
  min,
  max,
  description,
}: FormFieldProps) {
  const fieldId = id || label.toLowerCase().replace(/\s+/g, '-');

  const baseInputClasses =
    'w-full px-4 py-2 border border-gray-200 rounded-lg bg-white disabled:bg-beige disabled:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary';

  return (
    <div>
      <label
        htmlFor={fieldId}
        className="block text-sm font-medium mb-2 text-text-dark"
      >
        {label}
        {required && ' *'}
      </label>
      {description && (
        <p className="text-xs text-text-secondary mb-2">{description}</p>
      )}
      {type === 'select' && options ? (
        <select
          id={fieldId}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={baseInputClasses}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={fieldId}
          type={type}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={baseInputClasses}
          placeholder={placeholder}
          min={min}
          max={max}
        />
      )}
      {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
    </div>
  );
}
